/* ============================================
   BOOKMARK BUTTON COMPONENT
   The Creative Orchestra - GEMINI Framework
   Bookmark save and resume functionality
   ============================================ */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bookmark, 
  BookmarkCheck, 
  BookOpen,
  Check,
  X,
} from 'lucide-react';
import useBookmark from '@hooks/useBookmark';
import useReadingProgress from '@hooks/useReadingProgress';
import { getChapterById } from '@data';

/**
 * Bookmark Button Component
 * Saves current reading position
 * 
 * @param {Object} props
 * @param {string} props.chapterId - Current chapter ID
 * @param {string} props.chapterTitle - Current chapter title
 * @param {string} props.variant - 'default' | 'floating' | 'mini'
 */
const BookmarkButton = ({
  chapterId,
  chapterTitle,
  variant = 'default',
  className = '',
}) => {
  const [showFeedback, setShowFeedback] = useState(false);
  const { 
    saveBookmark, 
    isChapterBookmarked, 
    bookmark 
  } = useBookmark();
  const { progress, scrollPosition } = useReadingProgress();

  const isBookmarked = isChapterBookmarked(chapterId);

  const handleBookmark = useCallback(() => {
    saveBookmark(chapterId, {
      scrollPosition,
      scrollPercentage: progress,
      chapterTitle: chapterTitle || `Chapter ${chapterId}`,
    });

    setShowFeedback(true);
    setTimeout(() => setShowFeedback(false), 2000);
  }, [chapterId, chapterTitle, scrollPosition, progress, saveBookmark]);

  // Render based on variant
  if (variant === 'floating') {
    return (
      <FloatingBookmark
        isBookmarked={isBookmarked}
        showFeedback={showFeedback}
        onBookmark={handleBookmark}
        progress={progress}
        className={className}
      />
    );
  }

  if (variant === 'mini') {
    return (
      <MiniBookmark
        isBookmarked={isBookmarked}
        showFeedback={showFeedback}
        onBookmark={handleBookmark}
        className={className}
      />
    );
  }

  // Default variant
  return (
    <DefaultBookmark
      isBookmarked={isBookmarked}
      showFeedback={showFeedback}
      onBookmark={handleBookmark}
      progress={progress}
      className={className}
    />
  );
};

/**
 * Default Bookmark Button
 */
const DefaultBookmark = ({
  isBookmarked,
  showFeedback,
  onBookmark,
  progress,
  className,
}) => {
  return (
    <div className={`relative ${className}`}>
      <motion.button
        onClick={onBookmark}
        className={`
          flex items-center gap-2 px-4 py-2 rounded-lg
          transition-all duration-300
          ${isBookmarked
            ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30'
            : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10 hover:text-white'
          }
        `}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {isBookmarked ? (
          <BookmarkCheck className="w-4 h-4" />
        ) : (
          <Bookmark className="w-4 h-4" />
        )}
        <span className="text-sm font-medium">
          {isBookmarked ? 'Bookmarked' : 'Bookmark'}
        </span>
        <span className="text-xs text-gray-500">
          {Math.round(progress)}%
        </span>
      </motion.button>

      {/* Feedback tooltip */}
      <AnimatePresence>
        {showFeedback && (
          <motion.div
            className="absolute -top-12 left-1/2 -translate-x-1/2
              px-3 py-2 rounded-lg bg-green-500/20 border border-green-500/30
              text-green-400 text-sm flex items-center gap-2 whitespace-nowrap"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
          >
            <Check className="w-4 h-4" />
            Position saved!
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * Floating Bookmark Button
 */
const FloatingBookmark = ({
  isBookmarked,
  showFeedback,
  onBookmark,
  progress,
  className,
}) => {
  return (
    <motion.button
      onClick={onBookmark}
      className={`
        fixed bottom-24 right-6 z-40
        w-12 h-12 rounded-full
        flex items-center justify-center
        shadow-lg transition-all duration-300
        ${isBookmarked
          ? 'bg-indigo-500 text-white'
          : 'bg-black/60 backdrop-blur-sm text-gray-400 border border-white/10 hover:text-white'
        }
        ${className}
      `}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      title={`Bookmark at ${Math.round(progress)}%`}
    >
      <AnimatePresence mode="wait">
        {showFeedback ? (
          <motion.div
            key="check"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
          >
            <Check className="w-5 h-5" />
          </motion.div>
        ) : isBookmarked ? (
          <motion.div
            key="bookmarked"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
          >
            <BookmarkCheck className="w-5 h-5" />
          </motion.div>
        ) : (
          <motion.div
            key="bookmark"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
          >
            <Bookmark className="w-5 h-5" />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  );
};

/**
 * Mini Bookmark Button
 */
const MiniBookmark = ({
  isBookmarked,
  showFeedback,
  onBookmark,
  className,
}) => {
  return (
    <motion.button
      onClick={onBookmark}
      className={`
        p-2 rounded-lg transition-colors
        ${isBookmarked
          ? 'text-indigo-400'
          : 'text-gray-500 hover:text-white'
        }
        ${className}
      `}
      whileTap={{ scale: 0.9 }}
      title={isBookmarked ? 'Bookmarked' : 'Bookmark'}
    >
      {showFeedback ? (
        <Check className="w-5 h-5 text-green-500" />
      ) : isBookmarked ? (
        <BookmarkCheck className="w-5 h-5" />
      ) : (
        <Bookmark className="w-5 h-5" />
      )}
    </motion.button>
  );
};

/**
 * Resume Reading Card
 * Shows option to resume from bookmark
 */
export const ResumeReadingCard = ({ className = '' }) => {
  const navigate = useNavigate();
  const { bookmark, clearBookmark, getBookmarkDisplay } = useBookmark();
  const bookmarkDisplay = getBookmarkDisplay();

  if (!bookmark) return null;

  const handleResume = () => {
    navigate(`/novel/${bookmark.chapterId}`);
    // Note: Scroll position restore would happen in the NovelReader component
  };

  const handleDismiss = () => {
    clearBookmark();
  };

  return (
    <motion.div
      className={`p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/20 ${className}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-start gap-4">
        <div className="p-2 rounded-lg bg-indigo-500/20">
          <BookOpen className="w-5 h-5 text-indigo-400" />
        </div>

        <div className="flex-grow min-w-0">
          <h4 className="text-white font-medium mb-1">Continue Reading</h4>
          <p className="text-sm text-gray-400 truncate">
            {bookmarkDisplay?.chapterTitle}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {bookmarkDisplay?.progress} • {bookmarkDisplay?.timeAgo}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <motion.button
            onClick={handleResume}
            className="px-3 py-1.5 rounded-lg bg-indigo-500 text-white text-sm font-medium
              hover:bg-indigo-400 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Resume
          </motion.button>
          <motion.button
            onClick={handleDismiss}
            className="p-1.5 rounded-lg text-gray-500 hover:text-white
              hover:bg-white/10 transition-colors"
            whileTap={{ scale: 0.9 }}
            title="Dismiss"
          >
            <X className="w-4 h-4" />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

/**
 * Bookmark Indicator (shows if current page is bookmarked)
 */
export const BookmarkIndicator = ({ chapterId, className = '' }) => {
  const { isChapterBookmarked, bookmark } = useBookmark();

  if (!isChapterBookmarked(chapterId)) return null;

  return (
    <div className={`flex items-center gap-2 text-indigo-400 ${className}`}>
      <BookmarkCheck className="w-4 h-4" />
      <span className="text-sm">
        Bookmarked at {Math.round(bookmark?.scrollPercentage || 0)}%
      </span>
    </div>
  );
};

export default BookmarkButton;
